import { useState, useCallback, useRef } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useSelectionStore } from '../stores/selectionStore';
import { AppNode, Connection, NodeStatus } from '../types';
import { GENERATION_STATUS_FIELDS } from '../constants/protectedFields';
import { deepClone } from '../utils/clone';

// 多节点剪贴板结构（文档 3.2.1）
interface ClipboardData {
  nodes: AppNode[];          // 被复制的节点（深拷贝）
  connections: Connection[]; // 被复制节点之间的内部连接（from/to 均在 nodes 集合内）
  offset: number;            // 粘贴偏移计数器（每次粘贴递增，避免重叠）
}

interface Delta { deltaX: number; deltaY: number }

// 生成新节点 id（与 useNodeOperations 保持一致格式）
function genId(): string {
  return `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

export interface UseClipboardParams {
  saveHistory: () => void;
}

export interface UseClipboardReturn {
  copy: () => void;
  paste: () => void;
  duplicate: () => void;
  pasteAt: (sourceNodes: AppNode[], sourceConns: Connection[], delta: Delta) => string[];
  clipboard: ClipboardData | null;
}

export function useClipboard({ saveHistory }: UseClipboardParams): UseClipboardReturn {
  // clipboard 为内部 state（EditorLayout 不再持有），刷新丢失，与现状一致
  const [clipboard, setClipboard] = useState<ClipboardData | null>(null);
  const clipboardRef = useRef(clipboard);
  clipboardRef.current = clipboard;

  /**
   * 核心副本生成逻辑（文档 3.2.5 spawnCopies）：
   * - id 重生成
   * - 应用偏移 delta（统一保持相对布局）
   * - status = IDLE
   * - inputs 清空（由 addConnection 重建时回填）
   * - 生成状态字段清空（taskId 等），生成结果字段保留（副本携带结果）
   * - 重建内部连接（旧 id → 新 id 映射）
   * 返回新节点 id 数组
   */
  const spawnCopies = useCallback((
    sourceNodes: AppNode[],
    sourceConns: Connection[],
    delta: Delta,
  ): string[] => {
    if (sourceNodes.length === 0) return [];

    const nodeStore = useNodeStore.getState();
    const idMap = new Map<string, string>(); // 旧 id → 新 id

    const newNodes: AppNode[] = sourceNodes.map(n => {
      const newId = genId();
      idMap.set(n.id, newId);
      // 清空生成状态字段，保留生成结果字段
      const newData = { ...n.data };
      for (const f of GENERATION_STATUS_FIELDS) {
        (newData as Record<string, unknown>)[f] = undefined;
      }
      // 脚本节点：script 为嵌套结构，浅展开会与源节点共享引用（副本编辑污染源数据），需深拷贝
      const scriptData = (newData as { script?: unknown }).script;
      if (scriptData) {
        (newData as Record<string, unknown>).script = deepClone(scriptData);
      }
      return {
        ...n,
        id: newId,
        x: n.x + delta.deltaX,
        y: n.y + delta.deltaY,
        status: NodeStatus.IDLE,
        inputs: [],
        data: newData,
      };
    });

    // 重建内部连接（旧 id → 新 id），过滤映射失败的
    const newConns: Connection[] = sourceConns
      .map(c => ({
        from: idMap.get(c.from)!,
        to: idMap.get(c.to)!,
        targetHandle: c.targetHandle,
      }))
      .filter(c => c.from && c.to);

    // 写入 store：先加节点，再加连接（addConnection 会回填目标节点 inputs）
    newNodes.forEach(n => nodeStore.addNode(n));
    newConns.forEach(c => nodeStore.addConnection(c.from, c.to, c.targetHandle));

    return newNodes.map(n => n.id);
  }, []);

  // Ctrl+C：复制选中节点 + 内部连接（文档 3.2.2）
  const copy = useCallback(() => {
    const selectedIds = useSelectionStore.getState().getSelectedArray();
    if (selectedIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    const nodeMap = useNodeStore.getState().nodeMap;
    const selectedNodes: AppNode[] = [];
    for (const id of selectedIds) {
      const n = nodeMap.get(id);
      if (n) selectedNodes.push(n);
    }
    if (selectedNodes.length === 0) return;
    // 仅保留两端都在选中集合内的连接
    const internalConns = useNodeStore.getState().connections.filter(
      c => selectedSet.has(c.from) && selectedSet.has(c.to),
    );
    setClipboard({
      nodes: deepClone(selectedNodes),
      connections: deepClone(internalConns),
      offset: 0,
    });
  }, []);

  // Ctrl+V：粘贴，偏移量 = (offset + 1) * 50，固定方向（文档 3.2.3）
  const paste = useCallback(() => {
    const cb = clipboardRef.current;
    if (!cb || cb.nodes.length === 0) return;
    saveHistory();
    const offset = (cb.offset + 1) * 50;
    const newIds = spawnCopies(cb.nodes, cb.connections, { deltaX: offset, deltaY: offset });
    // 选中粘贴出来的新节点
    useSelectionStore.getState().setSelectedNodeIds(newIds);
    // 偏移计数器递增（注意需基于最新 clipboard，避免连续粘贴时引用过时）
    setClipboard({ ...cb, offset: cb.offset + 1 });
  }, [saveHistory, spawnCopies]);

  // Ctrl+D：原地复制粘贴，一步完成（文档 3.2.4）
  const duplicate = useCallback(() => {
    const selectedIds = useSelectionStore.getState().getSelectedArray();
    if (selectedIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    const nodeMap = useNodeStore.getState().nodeMap;
    const sourceNodes: AppNode[] = [];
    for (const id of selectedIds) {
      const n = nodeMap.get(id);
      if (n) sourceNodes.push(n);
    }
    if (sourceNodes.length === 0) return;
    const internalConns = useNodeStore.getState().connections.filter(
      c => selectedSet.has(c.from) && selectedSet.has(c.to),
    );
    saveHistory();
    // 原地偏移 50/50
    const newIds = spawnCopies(sourceNodes, internalConns, { deltaX: 50, deltaY: 50 });
    useSelectionStore.getState().setSelectedNodeIds(newIds);
  }, [saveHistory, spawnCopies]);

  // Alt+拖动复制：在拖动位移处生成副本（文档 3.2.5 pasteAt）
  // 不依赖 clipboard state，直接接收源数据，避免污染剪贴板
  const pasteAt = useCallback((
    sourceNodes: AppNode[],
    sourceConns: Connection[],
    delta: Delta,
  ): string[] => {
    return spawnCopies(sourceNodes, sourceConns, delta);
  }, [spawnCopies]);

  return { copy, paste, duplicate, pasteAt, clipboard };
}
